import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ActualServer } from '../types';

interface Language {
  code: string;
  nativeName: string;
  flag: string;
}

interface SettingsDropdownProps {
  activeServer: ActualServer | null;
  onManageServers: () => void;
}

// Language names in their native languages
const AVAILABLE_LANGUAGES: Language[] = [
  {
    code: 'en-US',
    nativeName: 'English (United States)',
    flag: '🇺🇸'
  },
  {
    code: 'ar-EG',
    nativeName: 'العربية (مصر)',
    flag: '🇪🇬'
  },
  {
    code: 'es-ES',
    nativeName: 'Español (España)',
    flag: '🇪🇸'
  },
  {
    code: 'fr-FR',
    nativeName: 'Français (France)',
    flag: '🇫🇷'
  }
];

const SettingsDropdown: React.FC<SettingsDropdownProps> = ({ activeServer, onManageServers }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleManageServers = () => {
    onManageServers();
    setIsOpen(false);
  };

  return (
    <div className="settings-dropdown" ref={dropdownRef}>
      <button 
        className={`settings-dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
        aria-label="Settings"
      >
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        <svg 
          className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none"
        >
          <path 
            stroke="currentColor" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="1.5" 
            d="m3 4.5 3 3 3-3"
          />
        </svg>
      </button>
      
      {isOpen && (
        <div className="settings-dropdown-menu">
          {/* Language Section */}
          <div className="settings-section">
            <div className="settings-section-header">
              <span className="settings-section-title">{t('language', { ns: 'common' })}</span>
            </div>
            <div className="language-options">
              {AVAILABLE_LANGUAGES.map((language) => (
                <button
                  key={language.code}
                  className={`language-option ${i18n.language === language.code ? 'selected' : ''}`}
                  onClick={() => handleLanguageSelect(language.code)}
                >
                  <span className="language-flag">{language.flag}</span>
                  <span className="language-name">{language.nativeName}</span>
                  {i18n.language === language.code && (
                    <span className="check-mark">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Server Management Section */}
          <div className="settings-section">
            <div className="settings-section-header">
              <span className="settings-section-title">{t('serverManagement', { ns: 'serverManagement' })}</span>
            </div>
            <div className="server-status-display">
              {activeServer ? (
                <div className="server-connected">
                  <div className="server-status">
                    <span className="status-icon">🟢</span>
                    <div className="server-info">
                      <strong>{activeServer.name}</strong>
                      <small>{activeServer.url}</small>
                      {activeServer.defaultBudgetName && (
                        <small className="default-budget">
                          📊 {activeServer.defaultBudgetName}
                          {activeServer.defaultBudgetCurrency && (
                            <> ({activeServer.defaultBudgetCurrency})</>
                          )}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="server-disconnected">
                  <span className="status-icon">🔴</span>
                  <span>{t('notConnected', { ns: 'server' })}</span>
                </div>
              )}
            </div>
            <button 
              onClick={handleManageServers}
              className="server-manage-btn"
            >
              {t('manage', { ns: 'server' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsDropdown; 